<template>
  <UIDialog
    :is-open
    :z-index
    size="full"
    title="Organize Family Structure"
    @close="handleClose"
  >
    <div class="ofs">
      <OrganizeFamilyStructureArea
        ref="areaRef"
        v-model:data="data"
        :paths
        :entry-settings
        :account-types
        :initialized="isTreeInitialized"
      >
        <OrganizeFamilyStructureTransactions 
          ref="transactionsRef"
          v-model:dates="transactionDatesMap"
          :initialized="isTreeInitialized"
          :transactions
          :transaction-settings
          @delete="handleDeleteTransaction"
        />
      </OrganizeFamilyStructureArea>
      <UIPreloader v-if="isSaving" message="Saving tree..." overlay  />
    </div>
    <template #footer>
      <UIButtonsGroup>
        <UIButtonSave
          label="Save"
          :loading="isSaving"
          :disabled="!transactions.length || isSaving"
          @click="handleClickSave"
        />
        <UIButtonClose :disabled="isSaving" @click="handleClose" />
      </UIButtonsGroup>
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, provide, useTemplateRef, watch } from 'vue'
import { cloneDeep, differenceWith, forOwn, get, isPlainObject, orderBy, set, unset } from 'lodash'

import { AnalyticsField, ModalClass, TransactionClass } from '@types'
import { OrganizeFamilyStructureItem, OrganizeFamilyStructurePath, OrganizeFamilyStructureTransaction } from './utils/types'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'
import { METADATA_FIELDS } from './utils/const'

import { generateId } from '@/entities/utils/helpers'
import { getCurrentDate } from '@/helpers/dates'
import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetsStore } from '@/store/assets'
import { useRepositoriesStore } from '@/store/repositories'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import OrganizeFamilyStructureTransactions from './OrganizeFamilyStructureTransactions.vue'
import { UIPreloader } from '@ui/core'
import { UIDialog } from '@ui/modals'
import { UIButtonClose, UIButtonsGroup, UIButtonSave } from '@ui/buttons'

import OrganizeFamilyStructureArea from './OrganizeFamilyStructureArea.vue'

type Props = {
  modal: ModalClass<any>
}

const props = defineProps<Props>()

const { warn, error, success } = useNotifications()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const assetsStore = useAssetsStore()
const repositoriesStore = useRepositoriesStore()
const transactionsSettingsStore = useTransactionsSettingsStore()
const transactionsStore = useTransactionsStore()

const originalPaths = ref<OrganizeFamilyStructurePath[]>([])
const data = ref<{ tree: Record<string, OrganizeFamilyStructureItem>; orphans: string[] }>({
  tree: {},
  orphans: [],
})

const isTreeInitialized = ref(false)
const isSaving = ref(false)

const transactionDatesMap = ref<Map<string, string>>(new Map())

const areaRef = useTemplateRef('areaRef')
const transactionsRef = useTemplateRef('transactionsRef')

const analyticsTree = computed(() => analyticsStore.module.tree.getList)

const transactionSettings = computed(() => transactionsSettingsStore.getList.find(item => item.name === 'add account'))
const entrySettings = computed(() => transactionSettings.value?.entries[0])

const accountTypes = computed(() => entrySettings.value?.account?.includes || [])
const assetTypes = computed(() => entrySettings.value?.asset?.includes || [])

const repositoryId = computed(() => repositoriesStore.getCurrentId)
const repoName = computed(() => repositoriesStore.getCurrentName)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

const paths = computed(() => getPaths(data.value.tree))

const transactions = computed(() => {
  // compare changes in original tree and current tree
  const oldDifferences = differenceWith(originalPaths.value, paths.value, (a, b) => JSON.stringify(a) === JSON.stringify(b))
  const newDifferences = differenceWith(paths.value, originalPaths.value, (a, b) => JSON.stringify(a) === JSON.stringify(b))
  
  const transactionsMap = new Map<string, OrganizeFamilyStructureTransaction>()
  
  const getKey = (path: string[]) => path.slice(-2).join('-')
  
  // if new node is added, create new transaction
  newDifferences.forEach(differenceNode => {
    const key = getKey(differenceNode.path)
    const assetId = differenceNode.path.at(-1)
    const accountId = differenceNode.path.at(-2)
    if (!assetId || !accountId || accountId === AnalyticsField.REPO_ROOT) return
    transactionsMap.set(key, { accountId, assetId, amount: differenceNode.amount })
  })
  oldDifferences.forEach(differenceNode => {
    const key = getKey(differenceNode.path)
    const assetId = differenceNode.path.at(-1)
    const accountId = differenceNode.path.at(-2)
    if (!assetId || !accountId || accountId === AnalyticsField.REPO_ROOT) return
    if (transactionsMap.has(key)) {
      // if transaction exists in newDifferences, decrease amount
      const transaction = transactionsMap.get(key)
      if (!transaction) return
      transaction.amount = (transaction.amount ?? 0) - differenceNode.amount
    } else {
      // if not, create new transaction with negative amount
      transactionsMap.set(key, { accountId, assetId, amount: -differenceNode.amount })
    }
  })
  return Array.from(transactionsMap.values()).filter(item => item.amount !== 0)
})

const getPaths = (node: OrganizeFamilyStructureItem) => {
  const res: OrganizeFamilyStructurePath[] = []
  const stack: Array<{ node: unknown; path: string[] }> = [{ node, path: [] }]
  while (stack.length) {
    const { node, path } = stack.pop()!
    if (!isPlainObject(node)) continue
    forOwn(node, (value, key) => {
      const nextPath = [...path, key]
      stack.push({ node: value, path: nextPath })
      if (key === AnalyticsField.REPO_ROOT || METADATA_FIELDS.includes(key)) return
      const amount = isPlainObject(value)
        ? ((value as OrganizeFamilyStructureItem).amount ?? 0)
        : 0
      res.push({ path: nextPath, amount })
    })
  }
  return res
}

const createTransactionInstance = (date: string | undefined, accountId: string, assetId: string, amount = 1) => {
  const instance = new TransactionClass({
    id: generateId(),
    date: date || analyticsDate.value,
    type: 'add account',
    description: '',
    entries: [
      {
        account_id: accountId,
        amount,
        asset_id: assetId,
      }
    ],
    repository_id: repositoryId.value,
  }, true, true)
  return instance
}

const isSamePath = (a: string[], b: string[]) => {
  return a.length === b.length && a.every((item, index) => item === b[index])
}

const hasOriginalPath = (path: string[]) => {
  return originalPaths.value.some(original => isSamePath(original.path, path))
}

const hasChildNodes = (node: unknown) => {
  if (!isPlainObject(node)) return false
  return Object.keys(node as Record<string, unknown>).some(key => !METADATA_FIELDS.includes(key))
}

const isAccountType = (assetId: string) => {
  const instance = assetsBunchStore.getElementById(assetId)
  const assetType = instance?.field<string>(ASSET_FIELD.TYPE).value
  return !!assetType && accountTypes.value.includes(assetType)
}

// Bring back the original node and any original descendants removed by the current tree edit.
const restoreOriginalPath = (tree: Record<string, OrganizeFamilyStructureItem>, targetPath: string[]) => {
  const nodesToRestore = originalPaths.value
    .filter(item => isSamePath(item.path, targetPath.slice(0, item.path.length)))
    .sort((a, b) => a.path.length - b.path.length)

  nodesToRestore.forEach(item => {
    const currentNode = get(tree, item.path)
    set(tree, item.path, {
      ...(isPlainObject(currentNode) ? currentNode : {}),
      path: item.path,
      amount: item.amount,
    })
  })
}

// Remove the new node, then prune new empty ancestors created only to host that node.
const removeNewPath = (tree: Record<string, OrganizeFamilyStructureItem>, path: string[]) => {
  const removedAssetIds: string[] = []
  const assetId = path.at(-1)
  if (assetId) {
    removedAssetIds.push(assetId)
  }

  unset(tree, path)

  for (let parentPath = path.slice(0, -1); parentPath.length > 1; parentPath = parentPath.slice(0, -1)) {
    const parentNode = get(tree, parentPath)
    if (hasOriginalPath(parentPath) || hasChildNodes(parentNode)) return removedAssetIds
    const parentAssetId = parentPath.at(-1)
    if (parentAssetId) {
      removedAssetIds.push(parentAssetId)
    }
    unset(tree, parentPath)
  }

  return removedAssetIds
}

// If a removed new node came from the unorganized list, make it available there again.
const handleOrphans = (tree: Record<string, OrganizeFamilyStructureItem>, removedAssetIds: string[], rolledBackAssetIds: string[]) => {
  // remove rolled back orphans
  data.value.orphans = data.value.orphans.filter(id => !rolledBackAssetIds.includes(id))
  // add removed orphans
  const treePaths = getPaths(tree)
  const orphanAssetIds = removedAssetIds.filter(assetId => {
    if (!isAccountType(assetId) || data.value.orphans.includes(assetId)) return false
    return !treePaths.some(item => item.path.at(-1) === assetId)
  })
  if (!orphanAssetIds.length) return

  data.value.orphans = [...data.value.orphans, ...orphanAssetIds]
}

// Transactions are computed from tree differences, so deleting one means reverting that diff in the tree.
const handleDeleteTransaction = (index: number) => {
  const transaction = transactions.value[index]
  const { accountId, assetId } = transaction
  if (!accountId || !assetId) return

  // remove date from transactionDatesMap
  transactionDatesMap.value.delete(`${accountId}-${assetId}`)

  const isRelatedPath = (item: OrganizeFamilyStructurePath) => item.path.at(-2) === accountId && item.path.at(-1) === assetId
  const currentNodes = paths.value.filter(isRelatedPath)
  const originalNodes = originalPaths.value.filter(isRelatedPath)
  if (!currentNodes.length && !originalNodes.length) return

  const tree = cloneDeep(data.value.tree)
  const removedAssetIds: string[] = []
  const rolledBackAssetIds: string[] = []

  currentNodes.forEach(currentNode => {
    const originalNode = originalNodes.find(item => isSamePath(item.path, currentNode.path))
    if (originalNode) {
      set(tree, [...currentNode.path, 'amount'], originalNode.amount)
      set(tree, [...currentNode.path, 'path'], originalNode.path)
    } else {
      removedAssetIds.push(...removeNewPath(tree, currentNode.path))
    }
  })

  originalNodes.forEach(originalNode => {
    if (currentNodes.some(item => isSamePath(item.path, originalNode.path))) return
    restoreOriginalPath(tree, originalNode.path)
    const assetId = originalNode.path.at(-1)
    const accountId = originalNode.path.at(-2)
    if (!assetId || !accountId) return
    rolledBackAssetIds.push(assetId, accountId)
  })

  data.value.tree = tree
  handleOrphans(tree, removedAssetIds, rolledBackAssetIds)
  areaRef.value?.commitHistory()
}

const nameFormatter = (key: string) => {
  if (key === AnalyticsField.REPO_ROOT) return repoName.value || UNKNOWN_VALUE
  const asset = assetsBunchStore.getElementById(key)
  return asset?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}
provide('nameFormatter', nameFormatter)

const handleClickSave = async () => {
  try {
    isSaving.value = true
    // store new transactions
    await transactionsStore.bulkStore(transactions.value.map(instance => {
      const date = transactionDatesMap.value.get(`${instance.accountId}-${instance.assetId}`)
      const transaction = createTransactionInstance(date, instance.accountId, instance.assetId, instance.amount)
      return transaction.get()
    }))
    // refresh data
    await Promise.all([assetsStore.fetch(), transactionsStore.fetch()])
    success({ message: 'Tree saved successfully' })
    analyticsStore.markAsDeprecated()
    props.modal.close()
  } catch (e) {
    if (!e) return
    const message = (e as Error).message
    error({ message })
    handleCatchedError(message)
  } finally {
    isSaving.value = false
  }
}

const handleClose = () => {
  if (!transactions.value.length) {
    props.modal.close()
    return
  }
  warn({
    message: 'Warning',
    description:
      'You have unsaved changes in the tree, would you like to continue editing?',
    actionRequired: true,
    actions: [
      {
        label: 'Continue editing',
        buttonVariant: 'primary',
        onClick: remove => {
          remove && remove()
        },
      },
      {
        label: 'Discard changes',
        buttonVariant: 'secondary',
        onClick: remove => {
          remove && remove()
          props.modal.close()
        },
      },
    ],
  })
}

// scroll to bottom when new transactions are added
watch(() => transactions.value.length, (value, prev) => {
  if (value <= prev) return
  transactionsRef.value?.scrollToBottom()
})

// initialize tree
watch(() => ({ tree: analyticsTree.value, settingsInitFlag: transactionsSettingsStore.initFlag }), async ({ tree, settingsInitFlag }) => {
  if (!tree?.length || !settingsInitFlag) return
  // shrink data to remove all leaf nodes
  const parentIds = new Set<string>()
  const orderedData = orderBy(tree, [item => item.path.length, item => item.balance], ['asc', 'desc'])
  orderedData.forEach(item => {
    const parentId = item.path.at(-2)
    if (typeof parentId === 'string') {
      parentIds.add(parentId)
    }
  })
  const assetsList = new Map(assetsBunchStore.getList)
  const treeData = {}
  // create tree structure with existing analytics tree
  orderedData.forEach(item => {
    const accountId = item.path.length > 2 ? item.path.at(-2) : AnalyticsField.REPO_ROOT
    const assetId = item.path.at(-1)
    if (!assetId || !accountId) return
    // check if asset is in types list
    const instance = assetsBunchStore.getElementById(assetId)
    if (!instance) return
    assetsList.delete(assetId)
    const assetType = instance.field<string>(ASSET_FIELD.TYPE).value
    if (assetType === 'other' && !parentIds.has(assetId)) return
    if (![...accountTypes.value, ...assetTypes.value].includes(assetType)) return
    const element = {
      path: item.path,
      amount: item.amount,
    }
    originalPaths.value.push(cloneDeep(element))
    set(treeData, item.path, element)
  })
  // get orphans
  const orphansData = Array.from(assetsList.values())
    .filter(item => accountTypes.value.includes(item.field<string>(ASSET_FIELD.TYPE).value))
    .map(item => item.id)
  // set data
  data.value.tree = treeData
  data.value.orphans = orphansData
  isTreeInitialized.value = true
}, { immediate: true })

onMounted(async () => {
  analyticsStore.setActive('OrganizeFamilyStructure', ['tree'])
  if (!transactionsSettingsStore.initFlag) {
    await transactionsSettingsStore.fetch()
  }
})

onBeforeUnmount(() => {
  analyticsStore.setInActive('OrganizeFamilyStructure')
})
</script>

<style>
.ofs {
  @apply min-h-96 flex-auto;
  @apply grid grid-cols-1 sm:grid-cols-[minmax(350px,35%)_auto] gap-4;
  @apply -mb-4 -mx-3 sm:mx-0;

  &__content {
    @apply min-h-[600px] sm:min-h-0;
    @apply flex flex-col gap-4;
    @apply overflow-hidden;
  }

  &__block {
    @apply relative;
    @apply p-2 sm:p-3;
    @apply border border-light-border;
    @apply bg-body-gray;
    @apply rounded-lg;
    @apply overflow-hidden;
  }

  &__header {
    @apply flex items-center justify-between;
    @apply text-xs uppercase;
    @apply text-light-text;
  }

  &__preloader {
    @apply w-full h-full;
    @apply flex flex-col items-center justify-center;
  }
}
</style>
